import { BLUE_600, GRAY_400, GRAY_900 } from 'constants/styling/theme'
import { BorderBoxWrapper, ShadowElevation } from 'components/common/BorderBoxWrapper'
import { KeyboardEvent, ReactNode, useMemo } from 'react'

import { SimpleImage } from 'components/common/SimpleImage'
import { SELECTION_RING_SX, useSelectionStyle } from 'components/common/SelectionStyle'
import Typography from '@mui/material/Typography'

/** @interface Props for the SelectableImageBox component. */
interface Props {
  /** The text to be displayed in the box. */
  text: string
  /** Image URL or icon as ReactNode. */
  image: ReactNode | string
  /** Width of the box in rem. */
  width?: number
  /** Height of the box in rem. */
  height?: number
  /** Specifies the size of the box shadow.  */
  elevation?: ShadowElevation
  /** Indicates whether the box is selected. */
  isSelected?: boolean
  /** Callback function triggered when the box is selected. */
  onSelect?: () => void
  /** Custom inactive border color. */
  inactiveBorderColor?: string
  /** Disabled the image box. */
  disabled?: boolean
}

/**
 * @component
 * SelectableImageBox displays a selectable box with an image or icon and text. It takes focus and is
 * picked on click, Enter or Space.
 * 
 * @example
 * <SelectableImageBox
 *   width={10}
 *   height={10}
 *   text="Slazy"
 *   isSelected={true}
 *   disabled={false}
 *   image="https://example.com/gudetama.png"
 *   onSelect={() => console.log('Slazy, lazy but still slaying!')}
 * />
 */
export const SelectableImageBox: React.FC<Props> = ({
  text,
  image,
  width,
  height,
  isSelected,
  elevation = 'none',
  inactiveBorderColor = GRAY_400,
  disabled,
  onSelect
}) => {
  const boxWidth = useMemo(() => width ? `${width}rem` : '100%', [width])
  const boxHeight = useMemo(() => height ? `${height}rem` : '100%', [height])

  const isRing = useSelectionStyle() === 'ring'

  const select = () => !disabled && onSelect?.()
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    select()
  }

  return (
    <BorderBoxWrapper
      elevation={elevation}
      padding={1.6}
      borderColor={isSelected ? (isRing ? SELECTION_RING_SX.borderColor : BLUE_600) : inactiveBorderColor}
      // A tile that shows a selection is a radio; one that only acts (a template) is a button.
      role={isSelected === undefined ? 'button' : 'radio'}
      aria-checked={isSelected === undefined ? undefined : isSelected}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      sx={{
        gap: '1rem',
        outline: 'none',
        '&:focus-visible': { boxShadow: `0 0 0 2px ${BLUE_600}` },
        ...(isSelected && isRing && { boxShadow: SELECTION_RING_SX.boxShadow }),
        display: 'flex',
        cursor: disabled ? 'not-allowed' : 'pointer',
        alignItems: 'center',
        flexDirection: 'column',
        width: boxWidth,
        height: boxHeight,
      }}
      onClick={select}
    >

      {typeof image === 'string'
        ? (
          <SimpleImage
            key={image}
            url={image}
            sx={{ width: '9.6rem', height: '9.6rem' }}
            borderRadius='none'
          />
        )
        : image // Render ReactNode for icons
      }

      <Typography variant='text-sm' fontWeight={500} color={GRAY_900}>
        {text}
      </Typography>

    </BorderBoxWrapper>
  )
}
